import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Download } from 'lucide-react';
import Logo from './Logo';
import { api } from '../lib/api';

const LINKS = [
  { label: 'About', to: '/about' },
  { label: 'Tournaments', to: '/tournaments' },
  { label: 'Leaderboard', to: '/leaderboard' },
  { label: 'Payouts', to: '/payouts' },
  { label: 'Streams', to: '/streams' },
  { label: 'Collaborations', to: '/collaborations' },
  { label: 'FAQ', to: '/#faq' },
  { label: 'Contact', to: '/#contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.settings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const downloadHref = settings?.android_download_url || '/#download';
  const external = downloadHref.startsWith('http');

  return (
    <header className="sticky top-0 z-50 bg-black/85 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {LINKS.map((l) =>
            l.to.includes('#') ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-[#9e9e9e] hover:text-white transition-colors duration-200"
              >
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 ${
                    isActive ? 'text-[#e8173a]' : 'text-[#9e9e9e] hover:text-white'
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nav-download-app-button"
            href={downloadHref}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="hidden sm:inline-flex items-center gap-2 bg-[#e8173a] hover:bg-[#c0102e] text-white text-xs font-extrabold uppercase tracking-wider px-5 py-2.5 rounded-lg transition-all duration-300 hover:shadow-[0_10px_30px_-10px_rgba(232,23,58,0.6)] active:scale-95"
          >
            <Download className="w-4 h-4" strokeWidth={2.6} />
            Download App
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className="py-3 px-2 text-sm font-semibold uppercase tracking-wider text-[#c9c9c9] hover:text-[#e8173a] transition-colors border-b border-white/5"
                >
                  {l.label}
                </Link>
              ))}
              <a
                data-testid="nav-mobile-download-app-button"
                href={downloadHref}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#e8173a] text-white text-sm font-extrabold uppercase tracking-wider px-5 py-3.5 rounded-lg"
              >
                <Download className="w-4 h-4" strokeWidth={2.6} />
                Download App
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
